import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, DatePicker, Switch, Upload, Button, Tag, Row, Col, Statistic, Input, Space, Tooltip, message, Typography } from 'antd';
import { DollarOutlined, UploadOutlined, PaperClipOutlined, ReloadOutlined, CheckCircleOutlined, ClockCircleOutlined, MailOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') : '';
const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;

export default function SalaryHisab() {
  const [period, setPeriod] = useState(dayjs().subtract(1, 'month').startOf('month'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState('');
  const month = period.month() + 1;
  const year = period.year();
  const isCurrent = period.isSame(dayjs(), 'month');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/staff/salary-sheet', { params: { month, year } });
      setRows(res.data.rows || []);
    } catch (err) { message.error(err.response?.data?.error || 'Load failed'); }
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const showMail = (mail) => {
    if (!mail) return;
    if (String(mail).startsWith('sent')) message.success(`Staff ko mail gaya (${String(mail).replace('sent to ', '')})`);
    else message.warning(`Staff ko mail nahi gaya: ${mail}`);
  };

  const save = async (r, status, proof) => {
    setBusy(r.user_id);
    try {
      const wasPaid = r.status === 'paid';
      const amount = wasPaid && r.amount != null ? r.amount : Math.round(Number(r.net_payable || 0));
      const fd = new FormData();
      fd.append('user_id', r.user_id);
      fd.append('month', month);
      fd.append('year', year);
      fd.append('amount', amount);
      fd.append('status', status);
      fd.append('mode', r.mode || 'bank');
      if (status === 'paid') fd.append('paid_date', wasPaid && r.paid_date ? r.paid_date : dayjs().format('YYYY-MM-DD'));
      if (r.notes) fd.append('notes', r.notes);
      fd.append('send_mail', '1');
      if (proof) fd.append('proof', proof);
      const res = await api.post('/staff/salary', fd);
      message.success(`${r.full_name} — ${period.format('MMMM YYYY')}: ${status === 'paid' ? 'Paid' : 'Unpaid'}${proof ? ' (screenshot saved)' : ''}`);
      showMail(res?.data?.mail);
      await load();
    } catch (err) { message.error(err.response?.data?.error || 'Save failed'); }
    setBusy(null);
  };

  const shown = rows.filter(r => !search || r.full_name.toLowerCase().includes(search.toLowerCase()));
  const paid = rows.filter(r => r.status === 'paid');
  const pending = rows.filter(r => r.status !== 'paid');
  const payableTotal = rows.reduce((s, r) => s + Number(r.status === 'paid' && r.amount != null ? r.amount : r.net_payable || 0), 0);
  const paidTotal = paid.reduce((s, r) => s + Number(r.amount || 0), 0);
  const pendingTotal = pending.reduce((s, r) => s + Number(r.net_payable || 0), 0);

  const columns = [
    { title: 'Staff', key: 'name', fixed: 'left', width: 180, render: (_, r) => <div><b>{r.full_name}</b><div style={{ fontSize: 12, opacity: 0.65 }}>{r.designation}</div></div> },
    { title: 'Monthly Salary', dataIndex: 'monthly_salary', width: 120, render: inr },
    { title: 'Present', key: 'p', width: 80, align: 'center', render: (_, r) => <Tag color="green">{r.present}</Tag> },
    { title: 'Absent', key: 'a', width: 80, align: 'center', render: (_, r) => <Tag color={r.absent ? 'red' : 'default'}>{r.absent}</Tag> },
    { title: 'Half Day', key: 'h', width: 80, align: 'center', render: (_, r) => <Tag color={r.half_day ? 'orange' : 'default'}>{r.half_day}</Tag> },
    { title: 'Leave / Holiday', key: 'l', width: 100, align: 'center', render: (_, r) => `${r.leave} / ${r.holiday}` },
    { title: 'Deduction', key: 'd', width: 110, render: (_, r) => r.total_deduction ? <span style={{ color: '#cf1322' }}>-{inr(r.total_deduction)}</span> : '—' },
    { title: 'Advance', key: 'adv', width: 100, render: (_, r) => r.advance_total ? <span style={{ color: '#d46b08' }}>-{inr(r.advance_total)}</span> : '—' },
    { title: 'Deni Hai (Net)', key: 'net', width: 120, render: (_, r) => <b style={{ fontSize: 15 }}>{inr(r.net_payable)}</b> },
    { title: 'Paid / Unpaid', key: 'st', width: 140, render: (_, r) => (
      <Switch checked={r.status === 'paid'} loading={busy === r.user_id} checkedChildren="Paid" unCheckedChildren="Unpaid"
        onChange={(v) => save(r, v ? 'paid' : 'pending')} style={{ background: r.status === 'paid' ? '#52c41a' : undefined }} />
    ) },
    { title: 'Screenshot', key: 'proof', width: 170, render: (_, r) => (
      <Space size={4} wrap>
        <Upload accept="image/*,.pdf" showUploadList={false} beforeUpload={(f) => { save(r, 'paid', f); return false; }}>
          <Tooltip title="Screenshot daalo — turant Paid ho jayega"><Button size="small" icon={<UploadOutlined />} loading={busy === r.user_id}>{r.proof_path ? 'Badlo' : 'Daalo'}</Button></Tooltip>
        </Upload>
        {r.proof_path && <a href={`${API_BASE}/${r.proof_path}`} target="_blank" rel="noreferrer"><PaperClipOutlined /> View</a>}
      </Space>
    ) },
    { title: 'Paid Amount / Date', key: 'pd', width: 150, render: (_, r) => r.status === 'paid'
      ? <div>{inr(r.amount)}<div style={{ fontSize: 12, opacity: 0.7 }}>{r.paid_date ? dayjs(r.paid_date).format('DD-MMM-YYYY') : '—'} · {(r.mode || '').toUpperCase()}</div></div> : '—' },
    { title: 'Kisne di', dataIndex: 'paid_by', width: 120, render: (v, r) => r.status === 'paid' ? (v || '—') : '—' },
    { title: 'Staff Mail', dataIndex: 'mail_status', width: 170, render: (v, r) => {
      if (r.status !== 'paid') return '—';
      if (!v) return <span style={{ opacity: 0.5 }}>—</span>;
      return <Tooltip title={v}><Tag icon={<MailOutlined />} color={String(v).includes('sent') ? 'green' : 'orange'}>{String(v).includes('sent') ? 'Mail gaya' : 'Nahi gaya'}</Tag></Tooltip>;
    } },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col>
            <Typography.Title level={3} style={{ margin: 0 }}><DollarOutlined /> Salary Hisab</Typography.Title>
            <div style={{ opacity: 0.7 }}>Mahina chuno — har staff ki salary, present/absent aur Paid/Unpaid. Paid karte hi staff ko mail jata hai.{isCurrent ? ' (Chalta mahina — roz attendance se update hota hai)' : ''}</div>
          </Col>
          <Col>
            <Space wrap>
              <DatePicker picker="month" value={period} onChange={(v) => v && setPeriod(v.startOf('month'))} allowClear={false} format="MMMM YYYY" size="large" />
              <Input.Search placeholder="Staff search" allowClear onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} size="large" />
              <Button icon={<ReloadOutlined />} onClick={load} size="large" />
            </Space>
          </Col>
        </Row>
      </Card>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card><Statistic title="Staff" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Kul Deni Hai" value={payableTotal} formatter={inr} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title={<span><CheckCircleOutlined style={{ color: '#52c41a' }} /> Paid ({paid.length})</span>} value={paidTotal} formatter={inr} valueStyle={{ color: '#389e0d' }} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title={<span><ClockCircleOutlined style={{ color: '#fa8c16' }} /> Baaki ({pending.length})</span>} value={pendingTotal} formatter={inr} valueStyle={{ color: '#d46b08' }} /></Card></Col>
      </Row>
      <Card title={`${period.format('MMMM YYYY')} — Salary`}>
        <Table rowKey="user_id" columns={columns} dataSource={shown} loading={loading} pagination={false} scroll={{ x: 1800 }} size="middle" />
      </Card>
    </div>
  );
}
